import '../../inputs/buttons/as-icon-button.ts';
import '../../overlays/as-tooltip-wrapper.ts';
import '../../text-and-lang/as-text.ts';
import { sendEv } from '../../../data/events.ts';
import styleText from './as-state-set.css?raw';
import { asApp } from '../../../util/singletons.ts';

/**
 * State management section component
 */
export class TrStateSet extends HTMLElement {
  static readonly tag = 'as-state-set' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      TrStateSet._sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <div set-id="header-state-set">
        <as-tooltip-wrapper show-on-hover placement="bottom" id="undo-tt">
          <as-icon-button
            file-name="header/undo.png" black disabled
            name="undo" disable-tooltips
          ></as-icon-button>
          <as-tooltip name="default"><as-text dict-key="tooltip.state.undo">Undo</as-text></as-tooltip>
          <as-tooltip name="warn">
            <div style="display:flex;align-items:center;gap:0.375rem;white-space:nowrap;">
              <as-text dict-key="tooltip.state.undoWarn">Undoing will clear redo stack!</as-text>
              <as-icon-button file-name="icons/check-mark.svg" black disable-tooltips data-confirm></as-icon-button>
              <as-icon-button file-name="icons/close.svg" black disable-tooltips data-dismiss></as-icon-button>
            </div>
          </as-tooltip>
        </as-tooltip-wrapper>
        <as-icon-button
          file-name="header/redo.png" black disabled
          tooltip-dict-key="tooltip.state.redo"
          tooltip-default-text="Redo"
          name="redo"
        ></as-icon-button>
      </div>
    `;

    this._setupEventListeners();
  }

  private _setupEventListeners(): void {
    const sr = this.shadowRoot!;

    sr.querySelector('as-icon-button[name="undo"]')?.addEventListener('as-button-click', () => {
      sendEv(window, 'as-undo');
    });

    sr.querySelector('as-icon-button[name="redo"]')?.addEventListener('as-button-click', () => {
      sendEv(window, 'as-redo');
    });
  }

  connectedCallback() {
    window.addEventListener('as-history-changed', this._handleHistoryChanged);
    window.addEventListener('as-undo-requires-confirm', this._handleUndoRequiresConfirm);
    this.addEventListener('as-tooltip-confirm', this._handleTooltipConfirm);

    if (asApp()?.hasAttribute('disable-undo-history')) {
      this.setUndoRedoState({ canUndo: true, canRedo: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('as-history-changed', this._handleHistoryChanged);
    window.removeEventListener('as-undo-requires-confirm', this._handleUndoRequiresConfirm);
    this.removeEventListener('as-tooltip-confirm', this._handleTooltipConfirm);
  }

  private _handleUndoRequiresConfirm = () => {
    (this.shadowRoot!.querySelector('#undo-tt') as any)?.showNamedTooltip('warn');
  };

  private _handleTooltipConfirm = () => {
    sendEv(window, 'as-undo-confirmed');
  };

  private _handleHistoryChanged = (e: Event) => {
    // When the host app manages its own undo/redo (disable-undo-history), the
    // built-in history stack is disabled and always reports canUndo/canRedo=false.
    // Leave the buttons enabled (force-enabled in connectedCallback) under the
    // host app's control instead of disabling them from the empty internal stack.
    if (asApp()?.hasAttribute('disable-undo-history')) return;
    const { canUndo, canRedo } = (e as CustomEvent).detail ?? {};
    this.setUndoRedoState({ canUndo: !!canUndo, canRedo: !!canRedo });
  };

  /** Enable or disable the undo/redo buttons. Leave a key out to keep that
   *  button as it is. Meant for apps running their own history under
   *  `disable-undo-history`; the built-in stack calls it too. */
  setUndoRedoState(state: { canUndo?: boolean; canRedo?: boolean }): void {
    const sr = this.shadowRoot!;
    if (state.canUndo !== undefined) sr.querySelector('as-icon-button[name="undo"]')?.toggleAttribute('disabled', !state.canUndo);
    if (state.canRedo !== undefined) sr.querySelector('as-icon-button[name="redo"]')?.toggleAttribute('disabled', !state.canRedo);
  }
}

customElements.define(TrStateSet.tag, TrStateSet); 
